const UserType = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  ORG_ADMIN: "org_admin",
  ORG_USER: "org_user",
};

const isPlatformAdminUserType = (userType) => userType === UserType.SUPER_ADMIN || userType === UserType.ADMIN;
const isOrgAdminUserType = (userType) => userType === UserType.ORG_ADMIN;
const isOrgUserType = (userType) => userType === UserType.ORG_USER;

const normalizeUserType = (value) => {
  switch (value) {
    case UserType.SUPER_ADMIN:
    case UserType.ADMIN:
    case UserType.ORG_ADMIN:
    case UserType.ORG_USER:
      return value;
    // Legacy numeric role compatibility while backend rollout settles.
    case 0:
      return UserType.SUPER_ADMIN;
    case -1:
    case -2:
    case -3:
      return UserType.ADMIN;
    case 1:
      return UserType.ORG_ADMIN;
    case 2:
    case 3:
      return UserType.ORG_USER;
    default:
      return null;
  }
};

const LicenseLevel = {
  COMMUNITY: 0,
  PROFESSIONAL: 1,
  BUSINESS: 2,
  ENTERPRISE: 3,
};

const LicenseLevelString = {
  COMMUNITY: "Community",
  PROFESSIONAL: "Professional",
  BUSINESS: "Business",
  ENTERPRISE: "Enterprise",
};

const CrsRuleNo = {
  CUSTOM: 400,
  CUSTOM_GLOBAL: 409,
  MIN_SD_SIG_RULE: 450,
  MAX_SD_SIG_RULE: 499,
  EXCLUSION_RULES_BEFORE_CRS: 900,
  INITIALIZATION: 901,
  OWASP_SQLI: 942,
  REQUEST_BLOCKING_EVALUATION: 949,
  RESPONSE_BLOCKING_EVALUATION: 959,
  EXCLUSION_RULES_AFTER_CRS: 999,
};

const CrsSecRuleId = {
  MIN_EXCEPTION: CrsRuleNo.EXCLUSION_RULES_BEFORE_CRS * 1000 + 1,
  MIN_CUSTOM: CrsRuleNo.CUSTOM * 1000,
  MAX_CUSTOM: CrsRuleNo.CUSTOM_GLOBAL * 1000 - 1,
  MIN_CUSTOM_GLOBAL: CrsRuleNo.CUSTOM_GLOBAL * 1000,
  MAX_CUSTOM_GLOBAL: CrsRuleNo.CUSTOM_GLOBAL * 1000 + 999,
  MIN_SD_SIG: CrsRuleNo.MIN_SD_SIG_RULE * 1000,
  MAX_SD_SIG: CrsRuleNo.MAX_SD_SIG_RULE * 1000 + 999,
  MIN_OWASP_MODSECURITY: CrsRuleNo.INITIALIZATION * 1000,
  MIN_SMART_PARSE: 411000,
  MAX_SMART_PARSE: 411999,
  REAL_MIN_SMART_PARSE: 411100,
  REAL_MAX_SMART_PARSE: 411199,
};

const WafType = {
  ALL: -1,
  SIGNATURE: 0,
  SENSEDEFENCE_SIGNATURE: 2,
  FIREWALL: 3,
  SMART_PARSE: 4,
};

const SmartParseSensitivity = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
};

const WafNodeType = {
  WAF_ENGINE: 0,
  RL_ENGINE: 1,
  BM_ENGINE: 2,
  AD_ENGINE: 3,
  OMB_SERVICE: 4,
  ES_ENGINE: 5,
  AU_ENGINE: 6,
  LLM_ENGINE: 7,
};

const EsNodeType = {
  MIN: 1,
  TIE_BREAKER: 1,
  DATA: 2,
  MAX: 2,
};

const WafAction = {
  DETECT: 0,
  BLOCK: 1,
  CHALLENGE: 2,
  ALL: 3,
};
const WafLevel = {
  FAST: 0,
  TRADEOFF: 1,
};
const WafStatus = {
  UNHEALTHY: 0,
  DISABLED: 1,
  DETECT: 2,
  BLOCK: 3,
};
const SigAnomalyScore = {
  VERY_LOW: 40,
  LOW: 25,
  MEDIUM: 10,
  HIGH: 5,
};
const ParanoiaLevel = {
  LEVEL1: 1,
  LEVEL2: 2,
  LEVEL3: 3,
  LEVEL4: 4,
};
const SslType = {
  OFF: 0,
  FLEXIBLE: 1,
  FULL: 2,
  FULL_STRICT: 3,
};

const TlsVersion = {
  TLS_1_0: 0,
  TLS_1_1: 1,
  TLS_1_2: 2,
  TLS_1_3: 3,
  TLS_1_4: 4,
};
const TlsProfile = {
  INTERMEDIATE: "intermediate",
  MODERN: "modern",
  CUSTOM: "custom",
};

const TlsProfileSummary = {
  intermediate: "TLS 1.2 + 1.3, Mozilla Intermediate ciphers (default).",
  modern: "TLS 1.3 only, AEAD ciphersuites.",
  custom: "Operator-supplied min/max version and ciphers.",
};

// Source of truth: layerd-pingora/docs/tls_catalog.json. Backend enforces
// the same allow-list — UI uses these to render the multi-select. Adding
// a cipher here without updating the backend catalog will fail the Joi
// validator with "unknown cipher token".
const TLS12_CIPHERS = [
  "ECDHE-ECDSA-AES128-GCM-SHA256",
  "ECDHE-RSA-AES128-GCM-SHA256",
  "ECDHE-ECDSA-AES256-GCM-SHA384",
  "ECDHE-RSA-AES256-GCM-SHA384",
  "ECDHE-ECDSA-CHACHA20-POLY1305",
  "ECDHE-RSA-CHACHA20-POLY1305",
  "DHE-RSA-AES128-GCM-SHA256",
  "DHE-RSA-AES256-GCM-SHA384",
];

const TLS13_CIPHERS = [
  "TLS_AES_256_GCM_SHA384",
  "TLS_CHACHA20_POLY1305_SHA256",
  "TLS_AES_128_GCM_SHA256",
];

// Backend Joi rejects a cipher_list with fewer than this many entries —
// avoids fragile single-cipher configs that go down on one OpenSSL update.
const TLS_MIN_CIPHER_COUNT_TLS12 = 4;


const ExpressionCondition = {
  EQUALS: "eq",
  NOT_EQUALS: "ne",
  CONTAINS: "cont",
  NOT_CONTAINS: "ncont",
  GREATER_THAN: "gt",
  LESS_THAN: "lt",
  GREATER_THAN_OR_EQUALS_TO: "ge",
  LESS_THAN_OR_EQUALS_TO: "le",
};
const ExceptionSkipRuleType = {
  ALL: 0,
  SIGNATURE: 2,
  SENSEDEFENCE_SIGNATURE: 3,
  SMART_PARSE: 4,
};
const SeverityLevel = {
  MIN: 0,
  INFO: 1,
  NOTICE: 2,
  WARNING: 3,
  ERROR: 4,
  CRITICAL: 5,
};

const SeverityName = {
  UNKNOWN: "Unknown",
  INFO: "Info",
  NOTICE: "Notice",
  WARNING: "Warning",
  ERROR: "Error",
  CRITICAL: "Critical",
};
const CertificateType = {
  SENSE_GUARD: 0,
  CUSTOM: 1,
};

const ConfigAction = {
  ALL: 0,
  WAF: 1,
  SSL: 2,
  EXCEPTION: 4,
  RATE_LIMIT: 8,
  BOT_MANAGEMENT: 16,
  DDOS: 32,
  LLM: 64,
  MAX: 127,
};
const DeleteUserAction = {
  MIN: 1,
  DELETE: 1,
  DISABLE: 2,
  MAX: 2,
};

const UserReportType = {
  MIN: 1,
  NEW: 1,
  ACTIVE: 2,
  DELETED: 3,
  MAX: 3,
};
const WAF_EDGE_DOMAIN = "edge.sensedefence.net";
const MAXHISTORY = 100;
const TIMEINTERVAL = 30000;
const FeatureDataType = {
  BOOLEAN: 0,
  NUMBER: 1,
};
const EmailType = {
  MIN: 1,
  WELCOME_EMAIL_VERIFICATION: 1,
  PASSWORD_RESET: 2,
  PAYMENT_SUCCESS: 3,
  PAYMENT_FAILURE: 4,
  SITE_ADD: 5,
  SITE_REMOVE: 6,
  DDOS_DETECTED: 7,
  CERTS_EXPIRING_SOON: 8,
  CERTS_EXPIRED: 9,
  MAX: 9,
};
const InvoiceType = {
  MIN: 0,
  STRIPE: 0,
  MANUAL: 1,
  MAX: 1,
};

const StripeInvoiceStatus = {
  DRAFT: "draft",
  OPEN: "open",
  PAID: "paid",
  UNCOLLECTIBLE: "uncollectible",
  VOID: "void",
};

const ExternalLogType = {
  MIN: 0,
  GENERAL: 0,
  ELASTIC_SEARCH: 1,
  SPLUNK: 2,
  SUMO_LOGIC: 3,
  AMAZON_CLOUD_WATCH: 4,
  AMAZON_CLOUD_WATCH_2: 5,
  GOOGLE_STACK_DRIVER: 6,
  IBM_QRADAR: 7,
  JOURNAL: 8,
  LOGENTRIES: 9,
  LOGGLY: 10,
  MS_OMS: 11,
  SYSLOG: 12,
  MAX: 12,
};

const ExternalLogTypeString = {
  GENERAL: "General",
  ELASTIC_SEARCH: "Elastic Search",
  SPLUNK: "Splunk",
  SUMO_LOGIC: "Sumo Logic",
  AMAZON_CLOUD_WATCH: "Amazon Cloud Watch",
  AMAZON_CLOUD_WATCH_2: "Amazon Cloud Watch",
  GOOGLE_STACK_DRIVER: "Google Stackdriver",
  IBM_QRADAR: "IBM QRadar",
  JOURNAL: "Journal",
  LOGENTRIES: "Logentries",
  LOGGLY: "Loggly",
  MS_OMS: "Microsoft Operation Management Suite",
  SYSLOG: "Syslog",
};

const getExternalLogTypeString = (logType) => {
  switch (logType) {
    case ExternalLogType.GENERAL:
      return ExternalLogTypeString.GENERAL;
    case ExternalLogType.ELASTIC_SEARCH:
      return ExternalLogTypeString.ELASTIC_SEARCH;
    case ExternalLogType.SPLUNK:
      return ExternalLogTypeString.SPLUNK;
    case ExternalLogType.SUMO_LOGIC:
      return ExternalLogTypeString.SUMO_LOGIC;
    case ExternalLogType.AMAZON_CLOUD_WATCH:
      return ExternalLogTypeString.AMAZON_CLOUD_WATCH;
    case ExternalLogType.AMAZON_CLOUD_WATCH_2:
      return ExternalLogTypeString.AMAZON_CLOUD_WATCH_2;
    case ExternalLogType.GOOGLE_STACK_DRIVER:
      return ExternalLogTypeString.GOOGLE_STACK_DRIVER;
    case ExternalLogType.IBM_QRADAR:
      return ExternalLogTypeString.IBM_QRADAR;
    case ExternalLogType.JOURNAL:
      return ExternalLogTypeString.JOURNAL;
    case ExternalLogType.LOGENTRIES:
      return ExternalLogTypeString.LOGENTRIES;
    case ExternalLogType.LOGGLY:
      return ExternalLogTypeString.LOGGLY;
    case ExternalLogType.MS_OMS:
      return ExternalLogTypeString.MS_OMS;
    case ExternalLogType.SYSLOG:
      return ExternalLogTypeString.SYSLOG;
    default:
      return "Unknown";
  }
};

const FireWallExpressionCondition = {
  EQUALS: "eq",
  NOT_EQUALS: "ne",
  CONTAINS: "cont",
  NOT_CONTAINS: "ncont",
  GREATER_THAN: "gt",
  LESS_THAN: "lt",
  GREATER_THAN_OR_EQUALS_TO: "ge",
  LESS_THAN_OR_EQUALS_TO: "le",
};

const FireWallExpressionConditionNumbers = {
  EQUALS: 1,
  NOT_EQUALS: 2,
  CONTAINS: 3,
  NOT_CONTAINS: 4,
  GREATER_THAN: 5,
  LESS_THAN: 6,
  GREATER_THAN_OR_EQUALS_TO: 7,
  LESS_THAN_OR_EQUALS_TO: 8,
};

const getConditionTitleByNumber = (cond) => {
  switch (cond) {
    case FireWallExpressionConditionNumbers.EQUALS:
      return "Equals";
    case FireWallExpressionConditionNumbers.NOT_EQUALS:
      return "Does not Equal";
    case FireWallExpressionConditionNumbers.CONTAINS:
      return "Contains";
    case FireWallExpressionConditionNumbers.NOT_CONTAINS:
      return "Does not Contain";
    case FireWallExpressionConditionNumbers.GREATER_THAN:
      return "Greater than";
    case FireWallExpressionConditionNumbers.LESS_THAN:
      return "Less than";
    case FireWallExpressionConditionNumbers.GREATER_THAN_OR_EQUALS_TO:
      return "Greater than OR equals to";
    case FireWallExpressionConditionNumbers.LESS_THAN_OR_EQUALS_TO:
      return "Less than OR equals to";
    default:
      return "Unknown";
  }
};

const FirewallAction = {
  MIN: 0,
  LOG: 0,
  BYPASS: 1,
  ALLOW: 2,
  CHALLENGE: 3,
  BLOCK: 4,
  DROP: 5,
  MAX: 5,
};


// === Locked Layerd vocab — added by FE alignment work ===

const FwMode = { OFF: 0, ENFORCE: 1, SHADOW: 2 };

const RateLimitMode = { OFF: 0, ENFORCE: 1, SHADOW: 2 };

const ChallengeKind = {
  INVISIBLE: "invisible",
  MANAGED: "managed",
  ADAPTIVE: "adaptive",
  INTERACTIVE: "interactive",
};

const RateLimitOnMissing = {
  SKIP: "skip",
  BUCKET_EMPTY: "bucket_empty",
  REJECT_RULE: "reject_rule",
};

// Closed taxonomy from feedback_ratelimit_locked.md (Phase-1 cred-stuffing).
const AuthFailureKind = [
  "bad_signature", "expired", "not_yet_valid", "bad_issuer", "bad_audience",
  "bad_alg", "not_found", "revoked", "bad_hmac", "bad_cert", "replay",
  "bad_apikey", "bad_password", "other",
];

// Per-site BM S3 sample strategy (smallint on sites table).
const BmS3SampleStrategy = { NONE: 0, INTERESTING: 1, ALL: 2 };

// BM netflow keying mode.
const NetflowKeyMode = { JA3: "ja3", JA4: "ja4", BOTH: "both" };

// Verdict-cache mode (global_config.verdictCache.mode).
const VerdictCacheMode = { OFF: "off", SHADOW: "shadow", ACTIVE: "active" };

// DDoS unblock reasons (server-side classification).
const DdosBlockReason = {
  TRIPLE: "triple",
  DOMAIN_IP: "domain_ip",
  DOMAIN_UID: "domain_uid",
  DOMAIN: "domain",
  FINGERPRINT: "fingerprint",
  UNKNOWN: "unknown",
};

const NGXFirewallAction = {
  LOG: 1,
  ALLOW: 2,
  CHALLENGE: 3,
  BLOCK: 4,
};

const CaptchaType = {
  RECAPTCHA_V2_CHECKBOX: 1,
  RECAPTCHA_V2_INVISIBLE: 2,
  RECAPTCHA_V3: 3,
  HCAPTCHA: 4,
};

const RateLimitMitigationTimeout = {
  THIRTY_SECONDS: 30,
  ONE_MINUTE: 60,
  TEN_MINUTES: 600,
  ONE_HOUR: 3600,
  ONE_DAY: 86400,
};

const RateLimitPeriod = {
  TEN_SECONDS: 10,
  ONE_MINUTE: 60,
  TWO_MINUTES: 120,
  FIVE_MINUTES: 300,
  TEN_MINUTES: 600,
  ONE_HOUR: 3600,
};

const RateLimitCharacteristicKey = {
  IP: "ip",
  SOURCE_IP: "src_ip",
  IP_WITH_NAT: "ip_nat",
  // Layerd canonical CIDR-bucketed IP — operator picks v4 + v6 bits.
  IP_CIDR: "ip/24/64",
  UID: "uid",
  FINGERPRINT: "fp",
  HOST: "host",
  URI_PATH: "uri.path",
  QUERY: "query",
  // Parameterised forms — UI shows a name input next to the key picker.
  HEADER_NAMED: "header.X-Custom",
  COOKIE_NAMED: "cookie.session",
  QUERY_NAMED: "query.token",
  HEADERS: "headers",
  COOKIE: "cookie",
  ASN: "asn",
  COUNTRY: "country",
  CITY: "city",
  JA3_FINGERPRINT: "ja3_fingerprint",
  JA4_FINGERPRINT: "ja4_fingerprint",
  // RL-V2 picker patch (2026-06-07): cred-stuffing matches the engine's
  // `CredStuffing { min_failures }` characteristic — defaults to
  // min_failures=5 when authored from the picker. Operators wanting a
  // non-default threshold edit the row JSON directly.
  CRED_STUFFING: "cred_stuffing",
  // RL-V2 picker patch (2026-06-07): api-anomaly matches Phase G.1
  // inline `ApiAnomaly { min_z }`. Defaults to z=3.0 (99.7% gaussian
  // outlier band) when authored from the picker.
  API_ANOMALY: "api_anomaly",
};

function getRateLimitCharacteristicTitle(key) {
  // Parameterised forms render with a literal pattern in the picker; the
  // actual <name> after the dot is captured separately.
  if (typeof key === "string") {
    if (key === "ip" || key.startsWith("ip/")) return key === "ip" ? "IP" : `IP CIDR ${key.slice(3)}`;
    if (key === "uid") return "Auth UID";
    if (key === "fp") return "Composite fingerprint";
    if (key === "host") return "Host";
    if (key === "uri.path") return "URI path";
    if (key.startsWith("header.")) return `Header (${key.slice(7) || "name"})`;
    if (key.startsWith("cookie.")) return `Cookie (${key.slice(7) || "name"})`;
    if (key.startsWith("query.")) return `Query param (${key.slice(6) || "name"})`;
  }
  switch (key) {
    case RateLimitCharacteristicKey.IP_WITH_NAT:
      return "IP with NAT support";
    case RateLimitCharacteristicKey.QUERY:
      return "Query (any)";
    case RateLimitCharacteristicKey.HEADERS:
      return "Headers (any)";
    case RateLimitCharacteristicKey.COOKIE:
      return "Cookie (any)";
    case RateLimitCharacteristicKey.ASN:
      return "ASN";
    case RateLimitCharacteristicKey.COUNTRY:
      return "Country";
    case RateLimitCharacteristicKey.CITY:
      return "City";
    case RateLimitCharacteristicKey.JA3_FINGERPRINT:
      return "JA3 Fingerprint*";
    case RateLimitCharacteristicKey.JA4_FINGERPRINT:
      return "JA4 Fingerprint*";
    case RateLimitCharacteristicKey.CRED_STUFFING:
      return "Cred-stuffing (≥5 failures)";
    case RateLimitCharacteristicKey.API_ANOMALY:
      return "API anomaly (z≥3.0)";
    default:
      return "Unknown";
  }
}

const DdosSensitivity = {
  MIN: 1,
  LOW: 1,
  MEIDUM: 2,
  HIGH: 3,
  MAX: 3,
};

const FeatureId = {
  MIN: 1,
  WEBSITES: 1,
  OWASP_SIGNATURE_WAF: 2,
  FREE_WILDCARD_CERTS: 3,
  REQUESTS: 4,
  DATA_RETENTION: 5,
  CUSTOM_CERTS_UPLOAD: 7,
  CUSTOM_WAF_RULES: 8,
  CUSTOM_BLOCK_PAGE: 9,
  TEAM_MANAGEMENT: 10,
  ROLE_BASED_ACCESS_CONTROL: 11,
  RATE_LIMIT_RULE: 12,
  ADVANCED_FEATURES_IN_RATE_LIMIT_RULE: 13,
  LOG_REQUEST_PAYLOAD: 14,
  SENSEDEFENCE_SIGNATURE_WAF: 15,
  B2B_SAML: 16,
  BOT_MANAGEMENT: 17,
  USE_CACHE: 18,
  SMART_PARSE_WAF: 19,
  MAX: 19,
};

const BotType = {
  MIN: 1,
  BAD: 1,
  GOOD: 2,
  MAX: 2,
};

const SenseDefenceOperator = {
  rx: "Rx",
  pm: "Pm",
  ge: "Ge",
  eq: "Eq",
  le: "Le",
  detectSQLi: "DetectSQLi",
  detectXSS: "DetectXSS",
};

const BotScore = {
  MIN_BAD: 1,
  MAX_BAD: 20,
  MIN_GOOD: 21,
  MAX_GOOD: 30,
  MAX_BOT: 30,
  MIN_HUMAN: 31,
  MAX_HUMAN: 100,
};

const ExpressionKeyField = {
  NONE: "none",
  SOURCE_IP: "src_ip",
  HOST_NAME: "host_name",
  URI: "uri",
  QUERY: "query",
  HEADER: "header",
  USER_AGENT: "ua",
  REFERER: "referer",
  COOKIE: "cookie",
  METHOD: "method",
  COUNTRY: "country",
  CITY_NAME: "city",
  AS_NUMBER: "asn",
  JA3_FINGERPRINT: "ja3_fingerprint",
  JA4_FINGERPRINT: "ja4_fingerprint",
  BOT_SCORE: "bot_score",
};

function getExpressionKeyTitle(key) {
  switch (key) {
    case ExpressionKeyField.SOURCE_IP:
      return "Source IP";
    case ExpressionKeyField.HOST_NAME:
      return "Host Name";
    case ExpressionKeyField.USER_AGENT:
      return "User Agent";
    case ExpressionKeyField.HEADER:
      return "Header";
    case ExpressionKeyField.QUERY:
      return "Query String";
    case ExpressionKeyField.COOKIE:
      return "Cookie";
    case ExpressionKeyField.URI:
      return "URI";
    case ExpressionKeyField.REFERER:
      return "Referer";
    case ExpressionKeyField.METHOD:
      return "HTTP Method";
    case ExpressionKeyField.COUNTRY:
      return "Country";
    case ExpressionKeyField.CITY_NAME:
      return "City";
    case ExpressionKeyField.AS_NUMBER:
      return "AS Number";
    case ExpressionKeyField.BOT_SCORE:
      return "Bot Score";
    case ExpressionKeyField.JA3_FINGERPRINT:
      return "JA3 Fingerprint*";
    case ExpressionKeyField.JA4_FINGERPRINT:
      return "JA4 Fingerprint*";
    default:
      return "Unknown";
  }
}

const CardLogo = ["amex", "cirrus", "diners", "dankort", "discover", "jcb", "maestro", "mastercard", "visa", "visaelectron"];
const CardLogoLabel = [
  "American Express",
  "Cirrus",
  "Diners Club",
  "DanKort",
  "DISCOVER",
  "JCB",
  "Maestro",
  "MasterCard",
  "VISA",
  "VISA Electron",
];

const getBrandLabel = (brand) => {
  const idx = CardLogo.findIndex((logo) => logo === brand);

  if (idx === -1) return "Unknown";

  return CardLogoLabel[idx];
};

const RuleOperator = {
  NONE: "none",
  DETECT_SQLI: "detectSQLi",
  DETECT_XSS: "detectXSS",
  EQUALS: "eq",
  CONTAINS: "contains",
  GREATER_THAN: "gt",
  LESS_THAN: "lt",
  GREATER_THAN_OR_EQUALS_TO: "ge",
  LESS_THAN_OR_EQUALS_TO: "le",
  PARTIAL_MATCH: "pm",
  REG_EXP_MATCH: "rx",
  BEGINS_WITH: "beginsWith",
  ENDS_WITH: "endsWith",
  MATCHES_IP: "ipMatch",
};

const RuleKeyField = {
  NONE: "none",
  SOURCE_IP: "src_ip",
  HOST_NAME: "host_name",
  URI: "uri",
  ARGS: "arg",
  ARGS_NAMES: "arg_name",
  REQUEST_HEADERS: "req_header",
  REQUEST_HEADER_NAMES: "req_header_name",
  USER_AGENT: "ua",
  COOKIE: "cookie",
  COOKIE_NAME: "cookie_name",
  METHOD: "method",
  URI_PATH: "path",
  QUERY: "query",
  REQUEST_BODY: "req_body",
  REQUEST_BODY_LENGTH: "req_body_len",
};

function getRuleKeyTitle(key) {
  switch (key) {
    case RuleKeyField.SOURCE_IP:
      return "Client IP address";
    case RuleKeyField.HOST_NAME:
      return "Host Name";
    case RuleKeyField.USER_AGENT:
      return "User-Agent";
    case RuleKeyField.REQUEST_HEADERS:
      return "Header Values";
    case RuleKeyField.REQUEST_HEADER_NAMES:
      return "Header Names";
    case RuleKeyField.ARGS:
      return "Parameter Values";
    case RuleKeyField.ARGS_NAMES:
      return "Parameter Names";
    case RuleKeyField.COOKIE:
      return "Cookie";
    case RuleKeyField.COOKIE_NAME:
      return "Cookie Names";
    case RuleKeyField.URI:
      return "URI (URL-decoded)";
    case RuleKeyField.METHOD:
      return "Method";
    case RuleKeyField.URI_PATH:
      return "URI Path (without query string)";
    case RuleKeyField.QUERY:
      return "Query String";
    case RuleKeyField.REQUEST_BODY:
      return "Body";
    case RuleKeyField.REQUEST_BODY_LENGTH:
      return "Body Length";
    default:
      return "Unknown";
  }
}

const isValidJSON = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

const RuleAction = {
  MIN: 0,
  ALLOW: 0,
  BLOCK: 1,
  LOG: 2,
  MAX: 2,
};

const RuleTransformation = {
  NONE: "none",
  REMOVE_NULLS: "removeNulls",
  LOWERCASE: "lowercase",
  DECODE_HTML_ENTITY_ESCAPE: "htmlEntityDecode",
  DECODE_JS_ESCAPE: "jsDecode",
  DECODE_CSS_ESCAPE: "cssDecode",
  DECODE_CLI_ESCAPE: "cmdLine",
  BASE64_DECODE: "base64Decode",
  URL_DECODE: "urlDecode",
  PATH_NORMALIZATION: "normalizePath",
  COMPRESS_WHITESPACE: "compressWhitespace",
  BASE64_ENCODE: "base64Encode",
  REMOVE_COMMENTS: "removeComments",
  HEX_DECODE: "hexDecode",
  HEX_ENCODE: "hexEncode",
  REMOVE_WHITE_SPACE: "removeWhitespace",
  REPLACE_NULLS: "replaceNulls",
  UPPERCASE: "uppercase",
  URL_DECODE_UNI: "urlDecodeUni",
  URL_ENCODE: "urlEncode",
  UTF8_TO_UNICODE: "utf8toUnicode",
  TRIM_LEFT: "trimLeft",
  TRIM_RIGHT: "trimRight",
  TRIM: "trim",
  LENGTH: "length",
};

const UnitPriceId = {
  MIN: 1,
  WAF_BASE_PRICE: 1,
  TRAFFIC_DELIVERED_PER_GB: 2,
  REQUESTS_DELIVERED_PER_10K: 3,
  ADDITIONAL_SITE_DOMAIN: 4,
  CERTIFICATE_DV_SNI: 5,
  BOT_MANAGEMENT_PRICE_SITE_DOMAIN: 6,
  BOT_MANAGEMENT_TRAFFIC_DELIVERED_PER_GB: 7,
  BOT_MANAGEMENT_REQUESTS_DELIVERED_PER_10K: 8,
  RATE_LIMITING_BASE_PRICE_SITE_DOMAIN: 9,
  RATE_LIMITING_TRAFFIC_DELIVERED_PER_GB: 10,
  RATE_LIMITING_REQUESTS_DELIVERED_PER_10K: 11,
  DDOS_BASE_PRICE: 12,
  DDOS_TRAFFIC_DELIVERED_PER_GB: 13,
  DDOS_REQUESTS_DELIVERED_PER_10K: 14,
  ENTERPRISE_SUPPORT: 15,
  PROFESSIONAL_SERVICES_INTEGRATION: 16,
  MAX: 16,
};

const BaseUnitPrice = {
  WAF_BASE_PRICE: 4000,
  TRAFFIC_DELIVERED_PER_GB: 0.1,
  REQUESTS_DELIVERED_PER_10K: 0.05,
  ADDITIONAL_SITE_DOMAIN: 60,
  CERTIFICATE_DV_SNI: 0,
  BOT_MANAGEMENT_PRICE_SITE_DOMAIN: 500,
  BOT_MANAGEMENT_TRAFFIC_DELIVERED_PER_GB: 0.1,
  BOT_MANAGEMENT_REQUESTS_DELIVERED_PER_10K: 0.05,
  AUTH_MANAGEMENT_PRICE_SITE_DOMAIN: 500,
  AUTH_MANAGEMENT_TRAFFIC_DELIVERED_PER_GB: 0.1,
  AUTH_MANAGEMENT_REQUESTS_DELIVERED_PER_10K: 0.05,
  RATE_LIMITING_BASE_PRICE_SITE_DOMAIN: 5,
  RATE_LIMITING_TRAFFIC_DELIVERED_PER_GB: 0.1,
  RATE_LIMITING_REQUESTS_DELIVERED_PER_10K: 0.05,
  DDOS_BASE_PRICE: 500,
  DDOS_TRAFFIC_DELIVERED_PER_GB: 0.1,
  DDOS_REQUESTS_DELIVERED_PER_10K: 0.05,
  ENTERPRISE_SUPPORT: 1000,
  PROFESSIONAL_SERVICES_INTEGRATION: 8000,
};

const DefaultDiscount = {
  DISCOUNT_1_YEAR: 0,
  DISCOUNT_3_YEAR: 20,
  DISCOUNT_5_YEAR: 25,
};

const LLMAPIProvider = {
  OPEN_AI: 1,
  ADOBE_FIREFLY: 2,
  AMAZON_BEDROCK: 3,
  ANTHROPIC: 4,
  AZURE: 5,
  CARTESIA: 6,
  CEREBRAS: 7,
  COHERE: 8,
  DEEPSEEK: 9,
  ELEVENLABS: 10,
  GOOGLE_STUDIO: 11,
  GOOGLE_VERTEX_AI: 12,
  GROK: 13,
  GROQ: 14,
  HUGGING_FACE: 15,
  MISTRAL: 16,
  OPEN_ROUTER: 17,
  PERPLEXITY: 18,
  CUSTOM: 19,
};

const getLLMAPIProviderTitle = (type) => {
  switch (type) {
    case LLMAPIProvider.OPEN_AI:
      return "Open AI";
    case LLMAPIProvider.ADOBE_FIREFLY:
      return "Adobe Firefly";
    case LLMAPIProvider.AMAZON_BEDROCK:
      return "Amazon Bedrock";
    case LLMAPIProvider.ANTHROPIC:
      return "Anthropic";
    case LLMAPIProvider.AZURE:
      return "Azure";
    case LLMAPIProvider.CARTESIA:
      return "Cartesia";
    case LLMAPIProvider.CEREBRAS:
      return "Cerebras";
    case LLMAPIProvider.COHERE:
      return "Cohere";
    case LLMAPIProvider.DEEPSEEK:
      return "Deepseek";
    case LLMAPIProvider.ELEVENLABS:
      return "Elevenlabs";
    case LLMAPIProvider.GOOGLE_STUDIO:
      return "Google Studio";
    case LLMAPIProvider.GOOGLE_VERTEX_AI:
      return "Google Vertex AI";
    case LLMAPIProvider.GROK:
      return "Grok";
    case LLMAPIProvider.GROQ:
      return "Groq";
    case LLMAPIProvider.HUGGING_FACE:
      return "Hugging face";
    case LLMAPIProvider.MISTRAL:
      return "Mistral";
    case LLMAPIProvider.OPEN_ROUTER:
      return "Open Router";
    case LLMAPIProvider.PERPLEXITY:
      return "Perplexity";
    case LLMAPIProvider.CUSTOM:
      return "Custom ...";
    default:
      return "Unknown";
  }
};

const getChatProviderFormat = (type) => {
  switch (type) {
    case LLMAPIProvider.OPEN_AI:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.ADOBE_FIREFLY:
      return [
        `[
    {"key": "prompt"}
]`,
        `[
    {"key": "unkown"}
]`,
      ];
    case LLMAPIProvider.AMAZON_BEDROCK:
      return [
        `[
    {"key": "inputText"}
]`,
        `[
    {"key": "outputText"}
]`,
      ];
    case LLMAPIProvider.ANTHROPIC:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "content"},
    {"key": "*"},
    {"key": "text"},
]`,
      ];
    case LLMAPIProvider.AZURE:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.CARTESIA:
      return [
        `[
    {"key": "transcript"}
]`,
        `[
    {"key": "unkown"}
]`,
      ];
    case LLMAPIProvider.CEREBRAS:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.COHERE:
      return [
        `[
    {"key": "message"}
]`,
        `[
    {"key": "text"}
]`,
      ];
    case LLMAPIProvider.DEEPSEEK:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.ELEVENLABS:
      return [
        `[
    {"key": "text"}
]`,
        `[
    {"key": "unkown"}
]`,
      ];
    case LLMAPIProvider.GOOGLE_STUDIO:
      return [
        `[
    {"key": "contents"},
    {"key": "*"},
    {"key": "parts"},
    {"key": "*"},
    {"key": "text"}
]`,
        `[
    {"key": "unkown"}
]`,
      ];
    case LLMAPIProvider.GROK:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.GROQ:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.HUGGING_FACE:
      return [
        `[
    {"key": "inputs"}
]`,
        `[
    {"key": "*"},
    {"key": "generated_text"}
]`,
      ];
    case LLMAPIProvider.MISTRAL:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.OPEN_ROUTER:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    case LLMAPIProvider.PERPLEXITY:
      return [
        `[{"key": "messages"},
{"key": "*"},
{
    "key": "content",
    "conditions": [
        {"key": "role", "operator": "eq", "value": "user"}
    ]
}]`,
        `[
    {"key": "choices"},
    {"key": "*"},
    {"key": "message"},
    {"key": "content"}
]`,
      ];
    default:
      return ["", ""];
  }
};

const LLMPromptScannersData = {
  ANONYMIZE: {
    ID: 1,
    NAME: "Anonymize",
    KEY: "anonymize",
  },
  BANCODE: {
    ID: 2,
    NAME: "Ban Code",
    KEY: "ban_code",
  },
  BAN_COMPETITORS: {
    ID: 3,
    NAME: "Ban Competitors",
    KEY: "ban_competitors",
  },
  BAN_SUBSTRINGS: {
    ID: 4,
    NAME: "Ban Substrings",
    KEY: "ban_substrings",
  },
  BAN_TOPICS: {
    ID: 5,
    NAME: "Ban Topics",
    KEY: "ban_topics",
  },
  CODE: {
    ID: 6,
    NAME: "Code",
    KEY: "code",
  },
  GIBBERISH: {
    ID: 7,
    NAME: "Gibberish",
    KEY: "gibberish",
  },
  INVISIBLE_TEXT: {
    ID: 8,
    NAME: "Invisible Text",
    KEY: "invisible_text",
  },
  LANGUAGE: {
    ID: 9,
    NAME: "Language",
    KEY: "language",
  },
  PROMPT_INJECTION: {
    ID: 10,
    NAME: "Prompt Injection",
    KEY: "prompt_injection",
  },
  REGEX: {
    ID: 11,
    NAME: "Regex",
    KEY: "regex",
  },
  SECRETS: {
    ID: 12,
    NAME: "Secrets",
    KEY: "secrets",
  },
  SENTIMENT: {
    ID: 13,
    NAME: "Sentiment",
    KEY: "sentiment",
  },
  TOKEN_LIMIT: {
    ID: 14,
    NAME: "Token Limit",
    KEY: "token_limit",
  },
  TOXICITY: {
    ID: 15,
    NAME: "Toxicity",
    KEY: "toxicity",
  },
};

const LLMOutputScannersData = {
  BANCODE: {
    ID: 1,
    NAME: "Ban Code",
    KEY: "ban_code",
  },
  BAN_COMPETITORS: {
    ID: 2,
    NAME: "Ban Competitors",
    KEY: "ban_competitors",
  },
  BAN_SUBSTRINGS: {
    ID: 3,
    NAME: "Ban Substrings",
    KEY: "ban_substrings",
  },
  BAN_TOPICS: {
    ID: 4,
    NAME: "Ban Topics",
    KEY: "ban_topics",
  },
  BIAS: {
    ID: 5,
    NAME: "Bias",
    KEY: "bias",
  },
  CODE: {
    ID: 6,
    NAME: "Code",
    KEY: "code",
  },
  DEANONYMIZE: {
    ID: 7,
    NAME: "Deanonymize",
    KEY: "deanonymize",
  },
  FACTUAL_CONSISTENCY: {
    ID: 8,
    NAME: "Factual Consistency",
    KEY: "factual_consistency",
  },
  GIBBERISH: {
    ID: 9,
    NAME: "Gibberish",
    KEY: "gibberish",
  },
  JSON: {
    ID: 10,
    NAME: "JSON",
    KEY: "json",
  },
  LANGUAGE_SAME: {
    ID: 11,
    NAME: "Language Same",
    KEY: "language_same",
  },
  LANGUAGE: {
    ID: 12,
    NAME: "Language",
    KEY: "language",
  },
  MALICIOUS_URLS: {
    ID: 13,
    NAME: "Malicious URLs",
    KEY: "malicious_urls",
  },
  NO_REFUSAL: {
    ID: 14,
    NAME: "No Refusal",
    KEY: "no_refusal",
  },
  READING_TIME: {
    ID: 15,
    NAME: "Reading Time",
    KEY: "reading_time",
  },
  REGEX: {
    ID: 16,
    NAME: "Regex",
    KEY: "regex",
  },
  RELEVANCE: {
    ID: 17,
    NAME: "Relevance",
    KEY: "relevance",
  },
  SENSITIVE: {
    ID: 18,
    NAME: "Sensitive",
    KEY: "sensitive",
  },
  SENTIMENT: {
    ID: 19,
    NAME: "Sentiment",
    KEY: "sentiment",
  },
  TOXICITY: {
    ID: 20,
    NAME: "Toxicity",
    KEY: "toxicity",
  },
  URL_REACHABILITY: {
    ID: 21,
    NAME: "URL Reachabitlity",
    KEY: "url_reachabitlity",
  },
};

const LLMRetryAttempts = [0, 1, 2, 3];
const LLMRetryIntervals = {
  "5s": 5,
  "10s": 10,
  "30s": 30,
  "1m": 60,
};

const LLMCodeScannerSupportedLanguages = [
  "ARM Assembly",
  "AppleScript",
  "C",
  "C#",
  "C++",
  "COBOL",
  "Erlang",
  "Fortran",
  "Go",
  "Java",
  "JavaScript",
  "Kotlin",
  "Lua",
  "Mathematica/Wolfram Language",
  "PHP",
  "Pascal",
  "Perl",
  "PowerShell",
  "Python",
  "R",
  "Ruby",
  "Rust",
  "Scala",
  "Swift",
  "Visual Basic .NET",
  "jq",
];

const scannerNameMap = {
  Anonymize: "anonymize",
  BanCode: "ban_code",
  BanCompetitors: "ban_competitors",
  BanSubstrings: "ban_substrings",
  BanTopics: "ban_topics",
  Code: "code",
  Gibberish: "gibberish",
  InvisibleText: "invisible_text",
  Language: "language",
  PromptInjection: "prompt_injection",
  Regex: "regex",
  Secrets: "secrets",
  Sentiment: "sentiment",
  TokenLimit: "token_limit",
  Toxicity: "toxicity",
  Bias: "bias",
  Deanonymize: "deanonymize",
  FactualConsistency: "factual_consistency",
  JSON: "json",
  LanguageSame: "language_same",
  MaliciousURLs: "malicious_urls",
  NoRefusal: "no_refusal",
  ReadingTime: "reading_time",
  Relevance: "relevance",
  Sensitive: "sensitive",
  URLReachabitlity: "url_reachabitlity",
};

const SITE_ID_ALL = "all";
const ZONE_ID_ALL = "all";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const RBACPermissions = {
  SITES_READ: "sites:read",
  SITES_WRITE: "sites:write",
  SITES_DELETE: "sites:delete",
  PROJECTS_READ: "projects:read",
  PROJECTS_WRITE: "projects:write",
  PROJECTS_DELETE: "projects:delete",
  WAF_READ: "waf:read",
  WAF_CONFIGURE: "waf:configure",
  RULES_READ: "rules:read",
  RULES_WRITE: "rules:write",
  RULES_DELETE: "rules:delete",
  SSL_READ: "ssl:read",
  SSL_CONFIGURE: "ssl:configure",
  BOT_READ: "bot:read",
  BOT_CONFIGURE: "bot:configure",
  RATE_LIMIT_READ: "rate_limit:read",
  RATE_LIMIT_WRITE: "rate_limit:write",
  RATE_LIMIT_DELETE: "rate_limit:delete",
  LOGS_READ: "logs:read",
  LOGS_EXPORT: "logs:export",
  STATS_READ: "stats:read",
  STATS_EXPORT: "stats:export",
  FIREWALL_READ: "firewall:read",
  FIREWALL_WRITE: "firewall:write",
  FIREWALL_DELETE: "firewall:delete",
  DDOS_READ: "ddos:read",
  DDOS_CONFIGURE: "ddos:configure",
  DAST_READ: "dast:read",
  DAST_MANAGE: "dast:manage",
  ZONES_READ: "zones:read",
  ZONES_WRITE: "zones:write",
  ZONES_DELETE: "zones:delete",
  USERS_READ: "users:read",
  USERS_MANAGE: "users:manage",
  SETTINGS_READ: "settings:read",
  SETTINGS_WRITE: "settings:write",
  API_KEYS_READ: "api_keys:read",
  API_KEYS_MANAGE: "api_keys:manage",
  BILLING_READ: "billing:read",
  BILLING_MANAGE: "billing:manage",
  SSO_READ: "sso:read",
  SSO_CONFIGURE: "sso:configure",
  NOTIFICATIONS_READ: "notifications:read",
  NOTIFICATIONS_MANAGE: "notifications:manage",
  LLM_READ: "llm:read",
  LLM_CONFIGURE: "llm:configure",
  AUTH_READ: "auth:read",
  AUTH_CONFIGURE: "auth:configure",
  API_SECURITY_READ: "api_security:read",
  API_SECURITY_CONFIGURE: "api_security:configure",
  API_SECURITY_WRITE: "api_security:write",
  API_SECURITY_DELETE: "api_security:delete",
};

const RBACPermissionCategories = {
  sites: { label: "Sites", permissions: ["sites:read", "sites:write", "sites:delete"] },
  projects: { label: "Projects", permissions: ["projects:read", "projects:write", "projects:delete"] },
  waf: { label: "WAF", permissions: ["waf:read", "waf:configure"] },
  rules: { label: "Rules", permissions: ["rules:read", "rules:write", "rules:delete"] },
  ssl: { label: "SSL", permissions: ["ssl:read", "ssl:configure"] },
  bot: { label: "Bot", permissions: ["bot:read", "bot:configure"] },
  rate_limit: { label: "Rate Limit", permissions: ["rate_limit:read", "rate_limit:write", "rate_limit:delete"] },
  logs: { label: "Logs", permissions: ["logs:read", "logs:export"] },
  stats: { label: "Stats", permissions: ["stats:read", "stats:export"] },
  firewall: { label: "Firewall", permissions: ["firewall:read", "firewall:write", "firewall:delete"] },
  ddos: { label: "DDoS", permissions: ["ddos:read", "ddos:configure"] },
  dast: { label: "DAST", permissions: ["dast:read", "dast:manage"] },
  zones: { label: "Zones (LLM)", permissions: ["zones:read", "zones:write", "zones:delete"] },
  users: { label: "Users", permissions: ["users:read", "users:manage"] },
  settings: { label: "Settings", permissions: ["settings:read", "settings:write"] },
  api_keys: { label: "API Keys", permissions: ["api_keys:read", "api_keys:manage"] },
  billing: { label: "Billing", permissions: ["billing:read", "billing:manage"] },
  sso: { label: "SSO", permissions: ["sso:read", "sso:configure"] },
  notifications: { label: "Notifications", permissions: ["notifications:read", "notifications:manage"] },
  llm: { label: "LLM", permissions: ["llm:read", "llm:configure"] },
  auth: { label: "Auth", permissions: ["auth:read", "auth:configure"] },
  api_security: {
    label: "API Security",
    permissions: ["api_security:read", "api_security:configure", "api_security:write", "api_security:delete"],
  },
};

const DefaultRoles = {
  ADMIN: "ADMIN",
  EDITOR: "EDITOR",
  VIEWER: "VIEWER",
  SECURITY_ANALYST: "SECURITY_ANALYST",
};
const ONE_DAY_IN_SEC = 24 * 60 * 60;

const DetectionFilterCategory = {
  TopWAFDetectionsByCountry: "country",
  TopUserAgent: "ua",
  TopPath: "uri",
  TopEventBySource: "source_ip",
  TopMethod: "method",
  TopDetection: "type",
};

export {
  FwMode, RateLimitMode, ChallengeKind, RateLimitOnMissing,
  AuthFailureKind, BmS3SampleStrategy, NetflowKeyMode, VerdictCacheMode, DdosBlockReason,
  UserType,
  normalizeUserType,
  isPlatformAdminUserType,
  isOrgAdminUserType,
  isOrgUserType,
  LicenseLevel,
  LicenseLevelString,
  CrsRuleNo,
  CrsSecRuleId,
  WafType,
  WafNodeType,
  EsNodeType,
  WafAction,
  WafLevel,
  WafStatus,
  ParanoiaLevel,
  SigAnomalyScore,
  SslType,
  TlsVersion,
  TlsProfile,
  TlsProfileSummary,
  TLS12_CIPHERS,
  TLS13_CIPHERS,
  TLS_MIN_CIPHER_COUNT_TLS12,
  ExpressionCondition,
  ExpressionKeyField,
  getExpressionKeyTitle,
  ExceptionSkipRuleType,
  SeverityLevel,
  SeverityName,
  CertificateType,
  ConfigAction,
  DeleteUserAction,
  UserReportType,
  WAF_EDGE_DOMAIN,
  MAXHISTORY,
  TIMEINTERVAL,
  FeatureDataType,
  EmailType,
  InvoiceType,
  StripeInvoiceStatus,
  ExternalLogType,
  getExternalLogTypeString,
  // ExternalLogTypeString,
  FireWallExpressionCondition,
  FireWallExpressionConditionNumbers,
  getConditionTitleByNumber,
  FirewallAction,
  NGXFirewallAction,
  CaptchaType,
  RateLimitMitigationTimeout,
  RateLimitPeriod,
  RateLimitCharacteristicKey,
  getRateLimitCharacteristicTitle,
  DdosSensitivity,
  FeatureId,
  BotType,
  SenseDefenceOperator,
  BotScore,
  CardLogo,
  getBrandLabel,
  RuleOperator,
  RuleKeyField,
  getRuleKeyTitle,
  isValidJSON,
  RuleAction,
  RuleTransformation,
  UnitPriceId,
  BaseUnitPrice,
  DefaultDiscount,
  LLMAPIProvider,
  getLLMAPIProviderTitle,
  getChatProviderFormat,
  LLMPromptScannersData,
  LLMOutputScannersData,
  LLMRetryAttempts,
  LLMRetryIntervals,
  LLMCodeScannerSupportedLanguages,
  scannerNameMap,
  SITE_ID_ALL,
  ZONE_ID_ALL,
  ONE_DAY_IN_MS,
  ONE_DAY_IN_SEC,
  DetectionFilterCategory,
  SmartParseSensitivity,
  RBACPermissions,
  RBACPermissionCategories,
  DefaultRoles,
};
